import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/ui/Card';
import AppIcon from '../../../components/AppIcon';
import { PageLayout } from '../../../components/ui/PageLayout';
import { PieChart } from 'lucide-react';

const reportCards = [
    { to: '/attendance/reports/clockings', icon: 'Clock' as const, labelKey: 'Clockings' },
    { to: '/attendance/reports/shifts', icon: 'CalendarDays' as const, labelKey: 'Shifts' },
    { to: '/attendance/reports/overtime', icon: 'Timer' as const, labelKey: 'Overtime' },
    { to: '/attendance/reports/work-sessions', icon: 'Briefcase' as const, labelKey: 'Work Sessions' },
    { to: '/attendance/reports/out-of-bounds', icon: 'MapPinOff' as const, labelKey: 'Out of bounds' },
    // The two leave reports (SPEC docs/reports-revamp/SPEC.md §9, §10). They sit LAST because they
    // are the only pair that reads leave records rather than attendance records — a reader scanning
    // the grid for "who was where" should reach the attendance five first.
    { to: '/attendance/reports/leaves', icon: 'CalendarOff' as const, labelKey: 'Leaves' },
    { to: '/attendance/reports/leave-balance', icon: 'Scale' as const, labelKey: 'Leave balance' },
];

// CO-583 — NOT one of the report cards above, and deliberately below them under its own
// heading: every card above opens a REPORT (pick a period, read figures, export a file); this
// one opens a QUEUE you act on. Folding it into the same grid would say they are the same kind
// of thing, and a reviewer looking for "what needs deciding" would keep opening the Overtime
// report instead.
const actionCard = {
    to: '/attendance/overtime-approvals',
    icon: 'ClipboardCheck' as const,
    labelKey: 'overtimeApprovals.title',
    hintKey: 'overtimeApprovals.cardHint',
    // Named like the report cards' aria-label above: a link whose accessible name is the whole
    // card (title + hint) reads as a paragraph in a screen reader's link list.
    ariaLabelKey: 'overtimeApprovals.cardAriaLabel',
};

export default function Reports() {
    const { t } = useTranslation();
    const companyPrimaryColor = useSelector(
        (state: any) => state.user.companyPrimaryColor,
    );
    const brandColor = companyPrimaryColor || 'var(--mowazaf-primary)';

    return (
        <PageLayout
            title={t('Reports')}
            description={t('Explore and download attendance-related reports.')}
            icon={PieChart}
            accentColor="blue"
            maxWidth="1600px"
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {reportCards.map((card) => (
                    <Card key={card.to} variant="interactive" className="h-full">
                        <RouterLink
                            to={card.to}
                            aria-label={t(`Open ${card.labelKey} report`)}
                            className="flex flex-col items-center gap-2 p-6 no-underline"
                        >
                            <AppIcon
                                name={card.icon}
                                size={28}
                                style={{ color: brandColor }}
                            />
                            <span className="font-semibold text-foreground">
                                {t(card.labelKey)}
                            </span>
                        </RouterLink>
                    </Card>
                ))}
            </div>

            <div className="mt-8">
                <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('overtimeApprovals.sectionTitle')}
                </h2>
                <Card variant="interactive">
                    <RouterLink
                        to={actionCard.to}
                        aria-label={t(actionCard.ariaLabelKey)}
                        className="flex items-start gap-3 p-5 no-underline"
                    >
                        <AppIcon
                            name={actionCard.icon}
                            size={22}
                            className="mt-0.5 shrink-0"
                            style={{ color: brandColor }}
                        />
                        <span className="flex flex-col gap-1">
                            <span className="font-semibold text-foreground">
                                {t(actionCard.labelKey)}
                            </span>
                            <span className="text-sm text-muted-foreground">
                                {t(actionCard.hintKey)}
                            </span>
                        </span>
                    </RouterLink>
                </Card>
            </div>
        </PageLayout>
    );
}
